// gazeMath.js — small linear-algebra helpers for the 5-point calibration.
//
// We fit a 2D affine transform that maps (iris_x, iris_y) in normalized image
// coordinates (0..1) to (screen_x, screen_y) in viewport pixels.
//
// Affine:  [sx]   [a b c] [ix]
//          [sy] = [d e f] [iy]
//                          [1 ]
//
// Solved with linear least squares over the 5 calibration points.

export function solveAffine(srcPoints, dstPoints) {
  // srcPoints: [[ix, iy], ...]  dstPoints: [[sx, sy], ...]
  if (srcPoints.length < 3 || srcPoints.length !== dstPoints.length) {
    throw new Error("solveAffine: need ≥3 matched points");
  }
  const n = srcPoints.length;
  // Build normal equations for X-row (a,b,c) and Y-row (d,e,f) independently.
  // For X: sum( [ix, iy, 1]^T @ [ix, iy, 1] ) @ [a,b,c]^T = sum( [ix,iy,1]^T * sx )
  const ATA = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  const ATbX = [0, 0, 0];
  const ATbY = [0, 0, 0];
  for (let i = 0; i < n; i++) {
    const [ix, iy] = srcPoints[i];
    const [sx, sy] = dstPoints[i];
    const row = [ix, iy, 1];
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) ATA[r][c] += row[r] * row[c];
      ATbX[r] += row[r] * sx;
      ATbY[r] += row[r] * sy;
    }
  }
  const X = solve3x3(ATA, ATbX);
  const Y = solve3x3(ATA, ATbY);
  return { a: X[0], b: X[1], c: X[2], d: Y[0], e: Y[1], f: Y[2] };
}

export function applyAffine(M, ix, iy) {
  return [M.a * ix + M.b * iy + M.c, M.d * ix + M.e * iy + M.f];
}

// Solve a 3x3 linear system with Cramer's rule. Sufficient for our problem.
function solve3x3(A, b) {
  const det = det3(A);
  if (Math.abs(det) < 1e-9) throw new Error("singular matrix in solve3x3");
  const dx = det3(replaceCol(A, 0, b)) / det;
  const dy = det3(replaceCol(A, 1, b)) / det;
  const dz = det3(replaceCol(A, 2, b)) / det;
  return [dx, dy, dz];
}

function det3(m) {
  return (
    m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1]) -
    m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0]) +
    m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0])
  );
}

function replaceCol(m, col, v) {
  return m.map((row, r) => row.map((val, c) => (c === col ? v[r] : val)));
}
